"use client";

import placeholderImage from "@/_assets/images/placeholderImage.svg";
import { Card } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/components/ui/carousel";
import { Link } from "@/i18n/navigation";
import { cn } from "@/lib/utils";
import { Blog } from "@/types/blogs/Blogs";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useLocale } from "next-intl";
import Image from "next/image";
import { useEffect, useState } from "react";

interface PropsType {
  title: string;
  blogs: Blog[];
}

interface NavButtonProps {
  direction: "prev" | "next";
  onClick: () => void;
  disabled: boolean;
  size?: "default" | "sm";
}

function NavButton({
  direction,
  onClick,
  disabled,
  size = "default",
}: NavButtonProps) {
  const Icon = direction === "prev" ? ChevronLeft : ChevronRight;

  return (
    <button
      type="button"
      aria-label={direction === "prev" ? "Previous slide" : "Next slide"}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "disabled:pointer-events-none disabled:opacity-50",
        "flex items-center justify-center rounded-full border border-border bg-background text-foreground transition-colors hover:bg-accent dark:border-white/20 dark:bg-white/5 dark:hover:bg-white/10",
        size === "default" ? "h-10 w-10" : "h-9 w-9",
      )}
    >
      <Icon className={size === "default" ? "h-5 w-5" : "h-4 w-4"} />
    </button>
  );
}

function formatBlogDate(dateString: string, locale: string) {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(locale, {
    month: "short",
    day: "2-digit",
    year: "numeric",
  });
}

function TravelTips({ title, blogs }: PropsType) {
  const locale = useLocale();
  const [api, setApi] = useState<CarouselApi>();
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    if (!api) return;

    const updateNavigation = () => {
      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    };

    updateNavigation();
    api.on("select", updateNavigation);
    api.on("reInit", updateNavigation);

    return () => {
      api.off("select", updateNavigation);
      api.off("reInit", updateNavigation);
    };
  }, [api]);

  if (!blogs || blogs.length === 0) return null;

  return (
    <section className="mt-16 bg-[#F8FAFC] py-16 dark:bg-background">
      <Carousel
        setApi={setApi}
        opts={{
          align: "start",
          direction: locale === "ar" ? "rtl" : "ltr",
        }}
        className="container w-full"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="mt-2 font-montserrat text-h2-base font-600 text-foreground md:text-h2-md xl:text-h2-xl">
              {title}
            </h2>
          </div>

          <div className="hidden shrink-0 items-center gap-3 md:flex">
            <NavButton
              direction="prev"
              onClick={() => api?.scrollPrev()}
              disabled={!canScrollPrev}
            />
            <NavButton
              direction="next"
              onClick={() => api?.scrollNext()}
              disabled={!canScrollNext}
            />
          </div>
        </div>

        <CarouselContent className="-ml-4 mt-10">
          {blogs.map((blog) => (
            <CarouselItem
              key={blog.slug}
              className="basis-full pl-4 md:basis-1/2 lg:basis-1/3 xl:basis-[28.5%]"
            >
              <Link
                href={`/blog${blog.category ? `/${blog.category.slug}` : ""}/${blog.slug}`}
              >
                <Card className="flex h-full flex-col overflow-hidden rounded-2xl border-none p-0 shadow-none transition-all dark:border dark:border-solid dark:border-white/15 dark:bg-[#1A1F24]">
                  <div className="relative h-[200px] w-full shrink-0">
                    <Image
                      src={blog.thumbnail || blog.image || placeholderImage}
                      alt={blog.name}
                      fill
                      sizes="(max-width: 639px) 90vw, (max-width: 1023px) 50vw, (max-width: 1279px) 33vw, 25vw"
                      className="object-cover"
                    />
                  </div>
                  <div className="flex flex-1 flex-col space-y-3 border-b border-l border-r border-border/40 p-6 dark:border-none">
                    <p className="font-montserrat text-[13px] font-500 not-italic text-muted-foreground">
                      {formatBlogDate(blog.date_to_show, locale)}
                    </p>
                    <h3 className="line-clamp-2 font-montserrat text-[18px] font-600 text-foreground">
                      {blog.name}
                    </h3>
                    <p className="line-clamp-3 text-[14px] font-400 not-italic text-muted-foreground">
                      {blog.sub_content}
                    </p>
                  </div>
                </Card>
              </Link>
            </CarouselItem>
          ))}
        </CarouselContent>

        <div className="mt-8 flex items-center justify-center gap-3 md:hidden">
          <NavButton
            direction="prev"
            onClick={() => api?.scrollPrev()}
            disabled={!canScrollPrev}
            size="sm"
          />
          <NavButton
            direction="next"
            onClick={() => api?.scrollNext()}
            disabled={!canScrollNext}
            size="sm"
          />
        </div>
      </Carousel>
    </section>
  );
}

export default TravelTips;
